import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/lib/seo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjb3Jwb3JhdGUlMjBjb25zdWx0aW5nJTIwYm9hcmRyb29tJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MTgyNjQ4MHww&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    n: "01",
    title: "Management Consultancy",
    body: "Practical support with the way your business is organised and managed — reviewing priorities, considering improvements and developing an approach suited to your commercial goals.",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    n: "02",
    title: "Business Advisory",
    body: "An outside perspective on the decisions facing your business, with support tailored to your circumstances.",
    span: "md:col-span-1",
  },
  {
    n: "03",
    title: "International Business Development",
    body: "Support for businesses exploring opportunities in different markets and jurisdictions.",
    span: "md:col-span-1",
  },
  {
    n: "04",
    title: "Business Support and Coordination",
    body: "Practical assistance with moving plans forward, organising information and coordinating with external advisers and service providers.",
    span: "md:col-span-3",
  },
];

export default function Home() {
  usePageMeta(
    "W Group Consulting — Business Consultancy and Advisory",
    "Management consultancy and business advisory for owners and leadership teams — developing businesses and exploring opportunities across markets and jurisdictions.",
    "/"
  );

  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden bg-ink" data-testid="home-hero">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Boardroom table beside floor-to-ceiling windows overlooking a city skyline"
            className="h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="mb-8 text-xs uppercase tracking-[0.35em] text-gold"
          >
            W Group Consulting — Business Consultancy and Advisory
          </motion.p>
          <h1 className="font-display text-5xl leading-[1.02] text-paper sm:text-6xl lg:text-8xl">
            <MaskedLines
              lines={[
                "Clarity for",
                <span key="l2" className="italic text-gold">decisive</span>,
                "moments.",
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-paper/75 md:text-lg"
          >
            W Group provides management consultancy and business advisory
            support to owners and leadership teams, helping them develop their
            businesses and explore opportunities across different markets and
            jurisdictions.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-3 bg-gold px-7 py-3.5 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:bg-paper"
            >
              Start a conversation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="border border-paper/40 px-7 py-3.5 text-sm tracking-wide text-paper transition-colors duration-300 hover:border-paper hover:bg-paper/10"
            >
              Explore our services
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 text-paper/50 lg:flex"
          aria-hidden="true"
        >
          <span className="text-xs uppercase tracking-[0.25em]">Scroll</span>
          <ArrowDown size={14} className="animate-bounce" />
        </motion.div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="home-manifesto">
        <Reveal>
          <p className="max-w-4xl font-display text-3xl leading-snug text-ink sm:text-4xl lg:text-5xl">
            Support shaped around each business — whether the priority is
            improving existing operations, pursuing growth or considering a{" "}
            <span className="italic text-gold">new jurisdiction</span>.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col gap-6 border-t border-ink/10 pt-10 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-md text-sm leading-relaxed text-gray-500">
              Practical support from the first discussion through to
              implementation.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-ink"
            >
              About the firm
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="bg-white py-24 lg:py-32" data-testid="home-services">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">What we do</p>
              <h2 className="font-display text-4xl text-ink sm:text-5xl">Business consultancy<br />and advisory</h2>
            </div>
            <Link
              to="/services"
              data-testid="home-services-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-ink"
            >
              All services
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="grid gap-px bg-ink/10 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08} className={s.span}>
                <Link
                  to="/services"
                  data-testid={`home-service-card-${s.n}`}
                  className="group flex h-full flex-col justify-between bg-white p-8 transition-colors duration-500 hover:bg-ink lg:p-10"
                >
                  <span className="text-xs tracking-[0.25em] text-gold">{s.n}</span>
                  <div className="mt-16">
                    <h3 className="font-display text-2xl text-ink transition-colors duration-500 group-hover:text-paper lg:text-3xl">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-gray-500 transition-colors duration-500 group-hover:text-paper/70">
                      {s.body}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-24 text-paper lg:py-32" data-testid="home-cta">
        <div className="mx-auto max-w-7xl px-6 text-center lg:px-10">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-[0.3em] text-gold">Next steps</p>
            <h2 className="mx-auto max-w-3xl font-display text-4xl leading-tight sm:text-5xl lg:text-6xl">
              Let&rsquo;s discuss your <span className="italic text-gold">next step</span>.
            </h2>
            <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-paper/70">
              Whether you are reviewing your current business or exploring a
              new opportunity, we can help you consider the way forward.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-contact"
              className="group mt-12 inline-flex items-center gap-3 bg-gold px-8 py-4 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:bg-paper"
            >
              Start a conversation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
